import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { domainMutationClient } from '../api/domainMutationClient';
import { domainRouterClient } from '../api/domainRouterClient';
import type { CognitoProfile } from '../utils/cognito-profile';
import {
  applyYstnjNightMode,
  persistYstnjNightModePreference,
  persistYstnjNightModePreferenceForUser,
  readYstnjNightModePreference,
  readYstnjNightModeProviderOverride,
  readYstnjNightModePreferenceForUser,
  toYstnjNightModeUserStorageKey,
  YSTNJ_NIGHT_MODE_STORAGE_KEY,
} from './nightMode';

type UseYstnjNightModeOptions = {
  profile?: CognitoProfile | null;
};

type RemoteSyncSnapshot = {
  userKey: string;
  enabled: boolean;
};

const normalizePreferenceIdentity = (value: string | null | undefined): string | null => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
};

const resolveAuthenticatedPreferenceKey = (
  profile: CognitoProfile | null | undefined,
): string | null => {
  if (!profile || profile.persona === 'guest') {
    return null;
  }
  return (
    normalizePreferenceIdentity(profile.userSub)
    ?? normalizePreferenceIdentity(profile.email)
    ?? normalizePreferenceIdentity(profile.username)
  );
};

const readInitialPreference = (authenticatedPreferenceKey: string | null): boolean => (
  readYstnjNightModePreferenceForUser(authenticatedPreferenceKey)
  ?? readYstnjNightModePreference()
);

const readRemotePreference = (preferences: unknown, fallback: boolean): boolean => {
  const darkModeEnabled = (preferences as { darkModeEnabled?: unknown } | null)?.darkModeEnabled;
  return typeof darkModeEnabled === 'boolean' ? darkModeEnabled : fallback;
};

export const useYstnjNightMode = (options: UseYstnjNightModeOptions = {}) => {
  const authenticatedPreferenceKey = resolveAuthenticatedPreferenceKey(options.profile);
  const providerOverride = readYstnjNightModeProviderOverride();
  const providerOverrideActive = providerOverride !== null;
  const authenticatedStorageKey = useMemo(
    () => toYstnjNightModeUserStorageKey(authenticatedPreferenceKey),
    [authenticatedPreferenceKey]
  );
  const [enabled, setEnabledState] = useState(() => providerOverride ?? readInitialPreference(authenticatedPreferenceKey));
  const enabledRef = useRef(enabled);
  const remoteLoadedUserKeyRef = useRef<string | null>(null);
  const lastSyncedRemoteRef = useRef<RemoteSyncSnapshot | null>(null);
  const userChangedDuringRemoteLoadRef = useRef(false);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (providerOverride === null) {
      return;
    }
    enabledRef.current = providerOverride;
    applyYstnjNightMode(providerOverride);
    setEnabledState(providerOverride);
  }, [providerOverride]);

  const persistRemotePreference = useCallback(async (userKey: string, nextEnabled: boolean) => {
    try {
      await domainMutationClient.updateUserPreferences({
        darkModeEnabled: nextEnabled,
      });
      lastSyncedRemoteRef.current = {
        userKey,
        enabled: nextEnabled,
      };
    } catch {
      // Keep the local preference responsive even if the profile update fails.
    }
  }, []);

  useEffect(() => {
    remoteLoadedUserKeyRef.current = null;
    lastSyncedRemoteRef.current = null;
    userChangedDuringRemoteLoadRef.current = false;

    if (providerOverrideActive) {
      return;
    }

    if (!authenticatedPreferenceKey) {
      return;
    }

    const cachedPreference = readYstnjNightModePreferenceForUser(authenticatedPreferenceKey);
    if (cachedPreference === null) {
      return;
    }

    enabledRef.current = cachedPreference;
    applyYstnjNightMode(cachedPreference);
    persistYstnjNightModePreference(cachedPreference);
    setEnabledState(cachedPreference);
  }, [authenticatedPreferenceKey, providerOverrideActive]);

  useEffect(() => {
    if (providerOverrideActive) {
      applyYstnjNightMode(providerOverride ?? false);
      return;
    }
    applyYstnjNightMode(enabled);
    persistYstnjNightModePreference(enabled);
    if (authenticatedPreferenceKey) {
      persistYstnjNightModePreferenceForUser(authenticatedPreferenceKey, enabled);
    }
  }, [authenticatedPreferenceKey, enabled, providerOverride, providerOverrideActive]);

  useEffect(() => {
    let cancelled = false;

    if (providerOverrideActive) {
      return () => {
        cancelled = true;
      };
    }

    if (!authenticatedPreferenceKey) {
      return () => {
        cancelled = true;
      };
    }

    const fallbackPreference = readInitialPreference(authenticatedPreferenceKey);

    domainRouterClient.query('getUserPreferences', {})
      .then((preferences) => {
        if (cancelled) {
          return;
        }
        const remotePreference = readRemotePreference(preferences, fallbackPreference);
        remoteLoadedUserKeyRef.current = authenticatedPreferenceKey;

        if (userChangedDuringRemoteLoadRef.current) {
          void persistRemotePreference(authenticatedPreferenceKey, enabledRef.current);
          return;
        }

        lastSyncedRemoteRef.current = {
          userKey: authenticatedPreferenceKey,
          enabled: remotePreference,
        };
        enabledRef.current = remotePreference;
        persistYstnjNightModePreference(remotePreference);
        persistYstnjNightModePreferenceForUser(authenticatedPreferenceKey, remotePreference);
        applyYstnjNightMode(remotePreference);
        setEnabledState(remotePreference);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        remoteLoadedUserKeyRef.current = authenticatedPreferenceKey;
        if (userChangedDuringRemoteLoadRef.current) {
          void persistRemotePreference(authenticatedPreferenceKey, enabledRef.current);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authenticatedPreferenceKey, persistRemotePreference, providerOverrideActive]);

  useEffect(() => {
    if (providerOverrideActive) {
      return;
    }
    if (!authenticatedPreferenceKey || remoteLoadedUserKeyRef.current !== authenticatedPreferenceKey) {
      return;
    }
    const lastSynced = lastSyncedRemoteRef.current;
    if (lastSynced?.userKey === authenticatedPreferenceKey && lastSynced.enabled === enabled) {
      return;
    }
    void persistRemotePreference(authenticatedPreferenceKey, enabled);
  }, [authenticatedPreferenceKey, enabled, persistRemotePreference, providerOverrideActive]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }
    if (providerOverrideActive) {
      return () => {};
    }
    const handleStorage = (event: StorageEvent) => {
      if (authenticatedStorageKey && event.key === authenticatedStorageKey) {
        const nextPreference = readYstnjNightModePreferenceForUser(authenticatedPreferenceKey);
        if (nextPreference !== null) {
          setEnabledState(nextPreference);
        }
        return;
      }
      if (event.key !== YSTNJ_NIGHT_MODE_STORAGE_KEY) {
        return;
      }
      if (authenticatedPreferenceKey && readYstnjNightModePreferenceForUser(authenticatedPreferenceKey) !== null) {
        return;
      }
      setEnabledState(readYstnjNightModePreference());
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [authenticatedPreferenceKey, authenticatedStorageKey, providerOverrideActive]);

  const setEnabled: Dispatch<SetStateAction<boolean>> = useCallback((nextEnabled) => {
    if (providerOverrideActive) {
      applyYstnjNightMode(providerOverride ?? false);
      setEnabledState(providerOverride ?? false);
      return;
    }
    userChangedDuringRemoteLoadRef.current = true;
    setEnabledState(nextEnabled);
  }, [providerOverride, providerOverrideActive]);

  const toggle = useCallback(() => {
    if (providerOverrideActive) {
      applyYstnjNightMode(providerOverride ?? false);
      setEnabledState(providerOverride ?? false);
      return;
    }
    userChangedDuringRemoteLoadRef.current = true;
    setEnabledState((current) => !current);
  }, [providerOverride, providerOverrideActive]);

  return {
    enabled,
    setEnabled,
    toggle,
  };
};
